import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Paperclip, Send, X } from 'lucide-react';

export function EmailEditor({ emailData = {}, onUpdate }) {
  const [showCcBcc, setShowCcBcc] = useState(false);

  const data = {
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    bodyType: 'rich',
    autoSend: false,
    attachments: [],
    ...emailData
  };

  const updateField = (field, value) => {
    onUpdate({ ...data, [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* To Field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">To</label>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setShowCcBcc(!showCcBcc)}
            >
              CC BCC Reply
            </Button>
          </div>
        </div>
        <Input
          placeholder="Email, e.g. alfred@wayne-enterprises.com"
          value={data.to}
          onChange={(e) => updateField('to', e.target.value)}
        />
      </div>

      {/* CC/BCC Fields (conditional) */}
      {showCcBcc && (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium">CC</label>
            <Input
              placeholder="CC emails"
              value={data.cc}
              onChange={(e) => updateField('cc', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">BCC</label>
            <Input
              placeholder="BCC emails"
              value={data.bcc}
              onChange={(e) => updateField('bcc', e.target.value)}
            />
          </div>
        </>
      )}

      {/* Subject Field */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Subject</label>
        <div className="relative">
          <Input
            placeholder="Subject"
            value={data.subject}
            onChange={(e) => updateField('subject', e.target.value)}
          />
        </div>
      </div>

      {/* Body Field */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Body</label>
        <Tabs
          value={data.bodyType}
          onValueChange={(value) => updateField('bodyType', value)}
        >
          <TabsList className="grid w-fit grid-cols-2 h-8">
            <TabsTrigger value="rich" className="text-xs">
              Rich Text
            </TabsTrigger>
            <TabsTrigger value="html" className="text-xs">
              HTML
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rich" className="mt-2">
            <Textarea
              placeholder="Start writing something..."
              value={data.body}
              onChange={(e) => updateField('body', e.target.value)}
              rows={8}
              className="resize-none"
            />
          </TabsContent>

          <TabsContent value="html" className="mt-2">
            <Textarea
              placeholder="<html>...</html>"
              value={data.body}
              onChange={(e) => updateField('body', e.target.value)}
              rows={8}
              className="resize-none font-mono text-xs"
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Attachment */}
      <button className="flex items-center gap-2 text-sm text-primary hover:underline">
        <Paperclip className="h-4 w-4" />
        Add attachment
      </button>

      {/* Auto-send Toggle */}
      <div className="flex items-center justify-between py-2">
        <label className="text-sm text-muted-foreground">
          Auto-send on task completion
        </label>
        <Switch
          checked={data.autoSend}
          onCheckedChange={(checked) => updateField('autoSend', checked)}
        />
      </div>

      {/* Send Test Button */}
      <Button variant="outline" size="sm" className="w-fit">
        <Send className="h-3 w-3 mr-2" />
        Send Test
      </Button>
    </div>
  );
}
